import { Asset, DEFAULT_PORT_LENDING_MARKET, Environment, Port, Share, WalletId } from "@port.finance/port-sdk";
import { useConnection } from "@solana/wallet-adapter-react";
import type { PublicKey } from "@solana/web3.js";
import { Connection } from "@solana/web3.js";
import type { LendingCollateralPosition, LendingCollateralPositionMap } from "@stepfinance/lending";
import type { LiquidityPoolMap } from "@stepfinance/liquidity";
import { useEffect, useState } from "react";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import { useMarkets } from "../../contexts/market";
import { calculateLPPrice } from "../../utils/liquidity/utils";
import { getTokenByMint } from "../../utils/tokenInfo";

// NOTE: This class should work as a temporary solution until Port finance implements this in their code.
class ExtendedPort extends Port {
  // Override of the original method to be able to return ExtendedPort
  static forMainNet({
    connection = new Connection("https://api.mainnet-beta.solana.com"),
    profile = Environment.forMainNet(),
    lendingMarket = DEFAULT_PORT_LENDING_MARKET,
  }: {
    connection?: Connection;
    profile?: Environment;
    lendingMarket?: PublicKey;
  }): ExtendedPort {
    return new ExtendedPort(connection, profile, lendingMarket);
  }

  async getLoanBySymbol(wallet: string): Promise<{ [key: string]: number }> {
    const result: { [key: string]: number } = {};

    const walletId = WalletId.fromBase58(wallet);
    const assets = this.getEnvironment().getAssetContext();
    const reserves = await this.getReserveContext();

    const portProfile = await this.getPortProfile(walletId);
    const loans = portProfile?.getLoans();
    loans?.forEach((loan) => {
      const reserve = reserves.findReserve(loan.getReserveId());
      if (!reserve) return;
      const symbol = assets.findConfig(reserve.getAssetMintId()).getSymbol();
      const asset = Asset.of(reserve.getAssetMintId(), loan.getAmount());
      result[symbol] = asset.toNumber(reserve.getQuantityContext());
    });

    return result;
  }

  async getDepositBySymbol(wallet: string): Promise<{ [key: string]: number }> {
    const result: { [key: string]: number } = {};

    const walletId = WalletId.fromBase58(wallet);
    const assets = this.getEnvironment().getAssetContext();
    const reserves = await this.getReserveContext();

    const balancesInWallet = await this.getShareAccount(walletId, reserves);
    balancesInWallet.forEach((balance) => {
      const shareMintId = balance.getMintId();
      const share = Share.fromTokenAccount(balance);
      const reserve = reserves.getByShareMintId(shareMintId);
      const symbol = assets.findConfig(reserve.getAssetMintId()).getSymbol();
      const asset = share.toAsset(reserve.getExchangeRatio());

      result[symbol] = asset.toNumber(reserve.getQuantityContext());
    });

    const portProfile = await this.getPortProfile(walletId);
    const balancesInObligation = portProfile?.getCollaterals();
    balancesInObligation?.forEach((collateral) => {
      const reserve = reserves.findReserve(collateral.getReserveId());
      if (!reserve) return;
      const share = Share.of(reserve.getShareMintId(), collateral.getAmount());
      const symbol = assets.findConfig(reserve.getAssetMintId()).getSymbol();
      const asset = share.toAsset(reserve.getExchangeRatio());
      result[symbol] = (result[symbol] || 0) + asset.toNumber(reserve.getQuantityContext());
    });

    return result;
  }
}

const getPortPositions = async (
  publicKey: string,
  saberPools: LiquidityPoolMap<"Saber">,
  port: ExtendedPort,
): Promise<LendingCollateralPositionMap> => {
  const reserveContext = await port.getReserveContext();
  const allReserves = reserveContext.getAllReserves();
  const depositData = await port.getDepositBySymbol(publicKey);
  const loanData = await port.getLoanBySymbol(publicKey);

  const collateralPositionMap: LendingCollateralPositionMap = {};

  for (const reserve of allReserves) {
    const mint = reserve.asset.getMintId().toBase58();
    const token = getTokenByMint(mint);
    let tokenSymbol: string | undefined = token?.symbol;
    if (tokenSymbol === "WSOL") {
      tokenSymbol = "SOL";
    } else if (tokenSymbol === "USDT-USDC") {
      tokenSymbol = "SLP";
    } else if (tokenSymbol === "wtUST") {
      tokenSymbol = "UST";
    }

    const depositApy = reserve.getSupplyApy().getPct()?.toOneBasedNumber(4);
    const borrowApy = reserve.getBorrowApy().getPct()?.toOneBasedNumber(4);
    if (tokenSymbol && depositApy !== undefined && borrowApy !== undefined) {
      const collateralPosition: LendingCollateralPosition = {
        deposit: {
          amount: 0,
          apy: depositApy,
        },
        borrow: {
          amount: 0,
          apy: borrowApy,
        },
        display: true,
      };

      if (tokenSymbol === "SLP") {
        const lp = saberPools[mint];
        collateralPosition.deposit = {
          ...collateralPosition.deposit,
          mintA: lp.mints[0],
          mintB: lp.mints[1],
          tokenPrice: calculateLPPrice(lp.supply, lp.liquidityInUSD),
        };
      }

      if (Object.prototype.hasOwnProperty.call(depositData, tokenSymbol) && depositData[tokenSymbol] > 0) {
        collateralPosition.deposit.amount = depositData[tokenSymbol];
        collateralPositionMap[mint] = collateralPosition;
      }

      if (Object.prototype.hasOwnProperty.call(loanData, tokenSymbol) && loanData[tokenSymbol] > 0) {
        collateralPosition.borrow.amount = loanData[tokenSymbol];
        collateralPositionMap[mint] = collateralPosition;
      }
    } else {
      console.warn("Unknown port position:", mint, tokenSymbol, depositApy, borrowApy);
    }
  }
  return collateralPositionMap;
};

export const usePort = () => {
  const { connected, publicKey } = useEnrichedWallet();
  const { marketsInitialised, refreshing } = useMarkets();
  const { connection } = useConnection();
  const { saberPools } = useLiquidity();

  const [port] = useState<ExtendedPort>(ExtendedPort.forMainNet({ connection }));

  const [collateralPositionMap, setCollateralPositionMap] = useState<LendingCollateralPositionMap>({});

  useEffect(() => setCollateralPositionMap({}), [connected]);

  useEffect(() => {
    const marketsRefreshFinished = !refreshing;
    if (publicKey && marketsInitialised && marketsRefreshFinished && Object.values(saberPools).length > 0) {
      void getPortPositions(publicKey.toBase58(), saberPools, port).then(setCollateralPositionMap);
    }
  }, [marketsInitialised, port, publicKey, saberPools, refreshing]);

  return { collateral: collateralPositionMap, yieldFarms: {} };
};
